import type { Channel, NotifyLevel } from '@nexo/types';
import { useContextMenu, useToast, type MenuEntries } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, CheckCheck, Link2, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { errorMessage } from '../../lib/forms';
import { canIn } from '../../lib/permissions';
import { isMuted } from '../notifications/channelNotify';
import { ChannelSettingsDialog } from './ChannelSettings';

const FOREVER = '2999-12-31T23:59:59.000Z';
const inMinutes = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();

/** Right-click menu of a conversation in the sidebar: read, mute, copy link, settings. */
export function useChannelMenu() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const menu = useContextMenu();
  const [settingsFor, setSettingsFor] = useState<Channel | null>(null);

  const notify = useMutation({
    mutationFn: ({
      channel,
      input,
    }: {
      channel: Channel;
      input: { level?: NotifyLevel; mutedUntil?: string | null };
    }) => api.channels.setNotifications(channel.id, input),
    onMutate: ({ channel, input }) =>
      cache.patchChannel(queryClient, channel.teamId, channel.id, () => ({
        ...(input.mutedUntil !== undefined ? { mutedUntil: input.mutedUntil } : {}),
      })),
    onError: (err, { channel }) => {
      cache.patchChannel(queryClient, channel.teamId, channel.id, () => ({
        mutedUntil: channel.mutedUntil,
      }));
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
    },
  });

  const markRead = (channel: Channel) => {
    cache.patchChannel(queryClient, channel.teamId, channel.id, () => ({
      unreadCount: 0,
      mentionCount: 0,
    }));
    void api.channels.markRead(channel.id);
  };

  const entries = (channel: Channel): MenuEntries => {
    const mute = (mutedUntil: string | null) => notify.mutate({ channel, input: { mutedUntil } });
    return [
      channel.unreadCount > 0 && {
        label: t('Marcar como leído'),
        icon: <CheckCheck />,
        onSelect: () => markRead(channel),
      },
      ...(isMuted(channel)
        ? [{ label: t('Reactivar avisos'), icon: <Bell />, onSelect: () => mute(null) }]
        : [
            {
              label: t('Silenciar 1 hora'),
              icon: <BellOff />,
              onSelect: () => mute(inMinutes(60)),
            },
            {
              label: t('Silenciar 8 horas'),
              icon: <BellOff />,
              onSelect: () => mute(inMinutes(8 * 60)),
            },
            {
              label: t('Silenciar hasta que lo reactive'),
              icon: <BellOff />,
              onSelect: () => mute(FOREVER),
            },
          ]),
      'separator',
      {
        label: t('Copiar enlace'),
        icon: <Link2 />,
        onSelect: () => {
          void navigator.clipboard.writeText(
            `${window.location.origin}/app/t/${channel.teamId}/c/${channel.id}`,
          );
          toast({ title: t('Enlace copiado'), tone: 'success' });
        },
      },
      channel.kind === 'channel' &&
        canIn(channel, 'manage_channels') && {
          label: t('Ajustes del canal'),
          icon: <Settings />,
          onSelect: () => setSettingsFor(channel),
        },
    ];
  };

  const element = (
    <>
      {menu.element}
      {settingsFor && (
        <ChannelSettingsDialog channel={settingsFor} open onClose={() => setSettingsFor(null)} />
      )}
    </>
  );

  return { open: (channel: Channel) => menu.open(() => entries(channel)), element };
}
